/**
 * "What Are Neuro Patty Sponges?" — SEO pillar article.
 *
 * Route: /resources/what-are-neuro-patty-sponges
 * Target queries:
 *   • "what are neuro patty sponges" / "what is a neuro sponge" / "cottonoid"
 *   • "neuro patty sponges history" / "neuro pattie material"
 *   • "Fabco vs Ray-Cot" / "American Surgical vs Fabco"
 *   • "neuro patty sponge sizes chart" / "neurosurgical pattie sizes"
 *   • "X-ray detectable neuro sponge"
 * Emits TechnicalArticle + FAQPage JSON-LD. Internally links to
 * /marketplace/american-surgical and /marketplace/product/fabco-neuro-patties.
 */
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, FileText, ShieldCheck, Package, ClipboardList } from "lucide-react";
import SEO, { technicalArticleSchema } from "@/components/SEO";
import SiteHeader from "@/components/SiteHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

// ────────────────────────────────────────────────────────────────
// Data blocks — kept declarative so they auto-generate JSON-LD
// ────────────────────────────────────────────────────────────────
const SIZE_CHART = [
  { size: "¼ × ¼ in", mm: "6 × 6 mm",     ray_cot: "60-02", fabco: "35500010", use: "Cortex protection · micro-hemostasis" },
  { size: "¼ × 1½ in", mm: "6 × 38 mm",   ray_cot: "60-16", fabco: "—",         use: "Retraction along a narrow sulcus" },
  { size: "¼ × 3 in", mm: "6 × 76 mm",    ray_cot: "60-04", fabco: "—",         use: "Long-tract retraction · cranial base" },
  { size: "½ × ½ in", mm: "13 × 13 mm",   ray_cot: "60-06", fabco: "35501110", use: "Everyday hemostasis · small oozing surface" },
  { size: "½ × 1 in", mm: "13 × 25 mm",   ray_cot: "60-07", fabco: "35501310", use: "Tumor bed hemostasis · sinus surgery" },
  { size: "½ × 1½ in", mm: "13 × 38 mm",  ray_cot: "60-08", fabco: "35501410", use: "Spine dura protection" },
  { size: "½ × 2 in", mm: "13 × 51 mm",   ray_cot: "60-29", fabco: "35501510", use: "Sub-frontal exposure · pediatric craniotomy" },
  { size: "½ × 3 in", mm: "13 × 76 mm",   ray_cot: "60-09", fabco: "35501610", use: "Retraction of temporal lobe · long dura" },
  { size: "¾ × ¾ in", mm: "19 × 19 mm",   ray_cot: "60-10", fabco: "35502210", use: "General hemostasis · cerebellar retraction" },
  { size: "1 × 1 in", mm: "25 × 25 mm",   ray_cot: "60-12", fabco: "35503310", use: "Broad cortical protection" },
  { size: "1 × 3 in", mm: "25 × 76 mm",   ray_cot: "60-13", fabco: "35503610", use: "Large retraction · trauma exposure" },
  { size: "1½ × 1½ in", mm: "38 × 38 mm", ray_cot: "60-14", fabco: "—",         use: "Deep bleeder tamponade" },
  { size: "1½ × 3 in", mm: "38 × 76 mm",  ray_cot: "60-22", fabco: "—",         use: "Large-vessel control · flap protection" },
  { size: "3 × 3 in", mm: "76 × 76 mm",   ray_cot: "60-15", fabco: "—",         use: "Wound edge protection · packing" },
];

const FAQS = [
  {
    q: "What is a neuro patty sponge?",
    a: "A neuro patty sponge — also called a cottonoid, neurosurgical pattie, or brain pattie — is a small, sterile, absorbent rayon square used during neurosurgery, spine, ENT, and ophthalmic procedures. Each patty has an X-ray detectable barium line and a securely attached locator string so it can be identified on a radiograph and retrieved from the surgical field. Neuro patties do two jobs: they protect delicate tissue (cortex, dura, cranial nerves) from surgical instruments, and they absorb blood and irrigation to keep the field visible during microsurgery.",
  },
  {
    q: "What is a cottonoid?",
    a: "\"Cottonoid\" is the surgical-team nickname for a neuro patty. Despite the name, modern neuro patties are made from rayon (not cotton) because rayon sheds virtually no fibers into the wound. The term dates back to the 1920s when early neurosurgeons — including Harvey Cushing at Peter Bent Brigham Hospital — used compressed cotton balls to control bleeding on the cortical surface. The name stuck even after the material changed.",
  },
  {
    q: "What are neuro patties made of?",
    a: "Modern neuro patties are made of medical-grade rayon (regenerated cellulose) — chosen because it is soft, highly absorbent, non-linting, and dissolves in aqueous solutions without releasing loose fibers. Every patty includes a radiopaque barium sulfate line (making it visible on X-ray) and a locator string (typically a bright blue polyester monofilament) so the surgical team can find and remove it before closure. All neuro patties sold in the U.S. are latex-free.",
  },
  {
    q: "Why are neuro patties X-ray detectable?",
    a: "Because a retained surgical sponge is one of the most-feared preventable events in the operating room. Neuro patties are used in dozens of pieces per case, packed into deep and narrow spaces, and are easy to lose count of during a long procedure. The Joint Commission's National Patient Safety Goal NPSG.14 and AORN standards both require radiopaque markers on all sponges used inside a wound. The barium sulfate line on each patty shows clearly on intraoperative and post-operative films.",
  },
  {
    q: "What is the difference between FABCO and American Surgical Ray-Cot® neuro patties?",
    a: "Both are U.S.-made, latex-free, X-ray detectable rayon patties with a locator string, packaged 10 patties per card, 20 pouches per box (200 patties per box). The core clinical performance is essentially interchangeable. The practical differences are: (1) American Surgical (Ray-Cot® / Delicot®) is manufactured in Salem, Massachusetts and marketed as \"12% more absorbent, 2× stronger\" than the leading competitor; Sterile Mate is an authorized distributor for the full American Surgical line. (2) FABCO neuro patties are sourced through Sterile Mate's specialty (Off-The-Grid / non-GPO) channel — we stock them for hospitals whose surgeons, GPO contract, or purchasing preference specifically calls for FABCO SKUs (35500010, 35501110, 35501310, 35501410, 35501510, 35501610, 35502210, 35503310, 35503610). Either line ships from us the same way; buyers pick whichever their protocol requires.",
  },
  {
    q: "How are neuro patties sterilized?",
    a: "Both FABCO and American Surgical neuro patties are sold sterile from the manufacturer. They are packaged 10 patties per count-verification card, sealed inside a peel-open sterile pouch, with 20 pouches per box (200 patties per box). Sterilization is validated by the manufacturer per ISO 11135 (ethylene oxide) or ISO 11137 (gamma) depending on the SKU. Do not re-sterilize opened patties — they are single-use.",
  },
  {
    q: "How many patty sizes does a typical neurosurgery service need to stock?",
    a: "Most SPD departments stock 8-12 sizes. The workhorse sizes are ½×½ in, ½×1 in, ½×1½ in, ¾×¾ in, and 1×1 in — these cover roughly 70% of use. Cranial base and skull-base teams add ¼×¼ in and ¼×3 in. Spine and trauma services add the larger 1×3 in, 1½×3 in, and 3×3 in patties. See the size chart on this page for the full range with FABCO and Ray-Cot® cross-references.",
  },
  {
    q: "Can I buy neuro patties without a GPO contract?",
    a: "Yes. Sterile Mate is a non-GPO friendly distributor. We serve hospitals, ambulatory surgery centers, and critical-access facilities across Texas, Louisiana, Alabama, and Mississippi with net-30 terms, certified small-business supplier-diversity credit, and no minimum order for existing accounts. Request pricing on either the American Surgical Ray-Cot® line or the FABCO specialty line and we'll return a firm quote within one business day.",
  },
  {
    q: "What are the standard packaging quantities for neuro patties?",
    a: "The industry-standard pack is: 10 patties per count-verification card, 1 card per sterile peel-open pouch, and 20 pouches per box — for a total of 200 patties per box. Both FABCO and American Surgical follow this convention. Most hospitals order full boxes; case-cart pull sheets typically call for 1-3 pouches of each size per procedure.",
  },
  {
    q: "Are neuro patties reusable?",
    a: "No. Neuro patties are single-use, sterile, disposable devices. Once opened they must be counted, used, and disposed of as biohazardous surgical waste per the facility's protocol. Attempting to re-sterilize or re-use them defeats the count-verification system, degrades the locator string, and risks infection.",
  },
];

export default function WhatAreNeuroPattiesPage() {
  const canonical = "/resources/what-are-neuro-patty-sponges";
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      technicalArticleSchema({
        headline: "What Are Neuro Patty Sponges? A Complete Guide to Cottonoids, Sizes, Materials, and Sterilization",
        description:
          "Complete clinical and procurement guide to neuro patty sponges (cottonoids): history, materials, X-ray detection, sterilization, FABCO vs American Surgical Ray-Cot® comparison, and a full size cross-reference chart.",
        url: canonical,
        datePublished: "2026-02-14",
        dateModified: "2026-02-14",
        keywords: [
          "what are neuro patty sponges",
          "neuro sponges",
          "cottonoids",
          "neurosurgical pattie",
          "FABCO neuro patties",
          "American Surgical Ray-Cot",
          "neuro patty sponge sizes",
          "X-ray detectable neuro sponge",
          "neurosurgical cottonoid material",
          "neuro patty sponges in stock",
        ],
      }),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="what-are-neuro-patties-page">
      <SEO
        canonical={canonical}
        title="What Are Neuro Patty Sponges? A Complete Guide to Cottonoids, Sizes & Sterilization"
        description="Clinical and procurement guide to neuro patty sponges (cottonoids): history, rayon material, X-ray detection, sterilization, FABCO vs American Surgical Ray-Cot® comparison, and a full size cross-reference chart."
        keywords={[
          "what are neuro patty sponges",
          "what is a cottonoid",
          "neurosurgical pattie",
          "FABCO vs Ray-Cot",
          "neuro patty sponge sizes chart",
          "X-ray detectable neuro sponge",
          "neuro patty sponges in stock",
        ]}
        schema={schema}
      />
      <SiteHeader />

      {/* ─────────── HERO ─────────── */}
      <section className="relative bg-gradient-to-br from-[#0a1a35] via-[#0b2447] to-[#0056A8] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(34,211,238,0.18),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-6 py-20">
          <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-cyan-300 mb-4">
            Clinical Resource · Neurosurgery · Sterile Processing
          </p>
          <h1 className="text-4xl md:text-6xl font-black leading-[1.05]">
            What Are Neuro Patty Sponges?
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/85 leading-relaxed">
            A complete guide to cottonoids — what they are, what they&apos;re made of, how they&apos;re sized,
            why they&apos;re X-ray detectable, and how the two brands most U.S. hospitals order
            (<strong>FABCO</strong> and <strong>American Surgical Ray-Cot<sup>®</sup></strong>) compare.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
              <Link to="/marketplace/american-surgical" data-testid="hero-cta-american-surgical">
                <Package className="w-5 h-5 mr-2" />
                Shop American Surgical Ray-Cot<sup>®</sup>
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
              <Link to="/marketplace/product/fabco-neuro-patties" data-testid="hero-cta-fabco">
                <ClipboardList className="w-5 h-5 mr-2" />
                RFQ · FABCO Neuro Patties
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ─────────── TL;DR ─────────── */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <p className="text-[10px] font-black tracking-widest text-slate-500 uppercase mb-2">TL;DR</p>
          <p className="text-slate-800 leading-relaxed">
            Neuro patty sponges (also called <em>cottonoids</em>) are small sterile rayon squares
            used during neurosurgery, spine, ENT, and ophthalmic procedures to protect delicate
            tissue and absorb blood. Every patty is X-ray detectable and has a locator string so
            the surgical team can track and retrieve it. In the U.S. the two most-ordered brands
            are <strong>American Surgical Ray-Cot<sup>®</sup></strong> (made in Salem, MA — the
            standard in most U.S. hospitals) and <strong>FABCO</strong> (an alternative many
            surgeons request by protocol). Sterile Mate stocks both.
          </p>
        </div>
      </section>

      {/* ─────────── TABLE OF CONTENTS ─────────── */}
      <section className="max-w-4xl mx-auto px-6 py-10">
        <p className="text-[10px] font-black tracking-widest text-slate-500 uppercase mb-3">On This Page</p>
        <ul className="grid sm:grid-cols-2 gap-2 text-sm">
          {[
            ["what-is-a-neuro-patty", "What is a neuro patty sponge?"],
            ["history", "A brief history of the cottonoid"],
            ["material", "What are neuro patties made of?"],
            ["xray", "Why the X-ray detectable line matters"],
            ["sizes", "Full size chart with FABCO ↔ Ray-Cot® cross-reference"],
            ["sterilization", "Sterilization & packaging"],
            ["brands", "FABCO vs American Surgical Ray-Cot® — how to choose"],
            ["procurement", "How to buy without a GPO"],
            ["faq", "FAQ"],
          ].map(([id, label]) => (
            <li key={id}>
              <a href={`#${id}`} className="text-blue-700 hover:text-blue-900 hover:underline flex items-center gap-1">
                <ArrowRight className="w-3 h-3" /> {label}
              </a>
            </li>
          ))}
        </ul>
      </section>

      {/* ─────────── SECTION 1 — WHAT IS A NEURO PATTY ─────────── */}
      <article id="what-is-a-neuro-patty" className="max-w-4xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-black text-slate-900 mb-4">What is a neuro patty sponge?</h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          A neuro patty sponge is a small, sterile, absorbent square used during any surgical procedure
          where the operative field is delicate and requires <strong>simultaneous tissue protection and
          fluid absorption</strong>. The classic use case is neurosurgery — draping a patty over the
          exposed cortex to shield the brain from a retractor blade while wicking away irrigation and
          blood — but the same devices are used in spine surgery (dura protection), ENT (nasal / sinus
          / mastoid procedures), ophthalmology (orbital and lacrimal work), and pediatric general
          surgery.
        </p>
        <p className="text-slate-700 leading-relaxed mb-4">
          Every neuro patty carries three features that distinguish it from a generic gauze sponge:
        </p>
        <ul className="space-y-2 mb-6">
          {[
            <><strong>A radiopaque barium sulfate line</strong> — visible on an intraoperative or
             recovery-room X-ray so a retained patty can be found before closure or on the film.</>,
            <><strong>A securely attached locator string</strong> — typically a bright blue polyester
             monofilament that hangs outside the wound so the scrub can pull the patty out cleanly.</>,
            <><strong>A count-verification card</strong> — patties ship 10 to a card so the circulator
             can confirm the count matches the surgical safety checklist before, during, and after
             the procedure.</>,
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-slate-700">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-slate-700 leading-relaxed">
          The combination of those three features is why the OR team calls out the count on every
          neuro sponge just as they do laparotomy sponges. A retained patty is a
          Joint-Commission-reportable event (National Patient Safety Goal NPSG.14).
        </p>
      </article>

      {/* ─────────── SECTION 2 — HISTORY ─────────── */}
      <article id="history" className="max-w-4xl mx-auto px-6 py-8 bg-slate-50 rounded-2xl my-8">
        <h2 className="text-3xl font-black text-slate-900 mb-4">A brief history of the cottonoid</h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          The nickname &quot;cottonoid&quot; is a century-old artifact of neurosurgical history. In the 1910s
          and 1920s, <strong>Harvey Cushing</strong> — often called the father of modern neurosurgery —
          began using small pledgets of compressed cotton to control bleeding on the exposed cortex
          at the Peter Bent Brigham Hospital in Boston. Cotton was cheap, absorbent, and gentle enough
          to lay directly against the brain. Surgeons called them &quot;cotton patties&quot; and then, informally,
          &quot;cottonoids.&quot;
        </p>
        <p className="text-slate-700 leading-relaxed mb-4">
          The material changed in the mid-20th century. Cotton fibers, however soft, can shed into
          the wound and act as a nidus for granuloma formation weeks or months after surgery. Rayon
          — a regenerated-cellulose fiber invented in France in 1855 and refined for medical use in
          the 1950s — is essentially non-linting. Once medical rayon became widely available,
          neurosurgical patties transitioned away from cotton within a decade. By the 1970s virtually
          all commercially sold neuro patties in the United States were rayon.
        </p>
        <p className="text-slate-700 leading-relaxed">
          The name never updated. Even today, in a modern operating room, a surgeon calling for a
          &quot;half-by-half cottonoid&quot; is asking for a rayon patty. The <strong>American Surgical
          Company</strong>, founded in Salem, Massachusetts, has manufactured neurosurgical patties
          continuously since 1919 and is one of the oldest continuously operating surgical-sponge
          manufacturers in the United States. FABCO — a mid-Atlantic manufacturer — has produced
          neuro patties under its own brand for more than three decades and is the second-most-common
          brand encountered in U.S. procurement systems.
        </p>
      </article>

      {/* ─────────── SECTION 3 — MATERIAL ─────────── */}
      <article id="material" className="max-w-4xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-black text-slate-900 mb-4">What are neuro patties made of?</h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          Modern neuro patty sponges are made from <strong>medical-grade rayon</strong> — a
          regenerated-cellulose fiber that is soft, highly absorbent, non-linting, and biocompatible
          with exposed neural tissue. Rayon fibers are extruded, cross-laid into a non-woven web,
          bonded, calendered flat, cut to size, and packed under cleanroom conditions.
        </p>
        <p className="text-slate-700 leading-relaxed mb-4">Key material properties every purchasing spec should call out:</p>
        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm mb-4">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-800">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Property</th>
                <th className="text-left px-4 py-3 font-bold">Requirement</th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              {[
                ["Base fiber", "100% medical-grade rayon (regenerated cellulose)"],
                ["Latex content", "Latex-free — required in every U.S. hospital"],
                ["Lint / fiber shedding", "Effectively zero — critical near exposed CNS tissue"],
                ["Absorbency", "≥ 12 g water per g of pattie (typical spec)"],
                ["Radiopaque marker", "Barium sulfate line embedded during manufacture"],
                ["Locator string", "Bright-colored polyester monofilament, securely attached"],
                ["Sterility", "Sterile, EO or gamma, single-use"],
                ["Biocompatibility", "ISO 10993-1 cytotoxicity, sensitization, irritation"],
              ].map(([k, v]) => (
                <tr key={k} className="border-t border-slate-200">
                  <td className="px-4 py-3 font-semibold text-slate-900">{k}</td>
                  <td className="px-4 py-3">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-slate-700 leading-relaxed">
          Both American Surgical and FABCO meet every one of these criteria. American Surgical
          publishes its rayon absorbency as <strong>12% higher</strong> than the leading competitor
          and its tensile strength as <strong>2× higher</strong> — a claim reflected in its Ray-Cot<sup>®</sup>
          product literature.
        </p>
      </article>

      {/* ─────────── SECTION 4 — X-RAY ─────────── */}
      <article id="xray" className="max-w-4xl mx-auto px-6 py-8 bg-slate-50 rounded-2xl my-8">
        <h2 className="text-3xl font-black text-slate-900 mb-4">Why the X-ray detectable line matters</h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          A retained surgical sponge is one of the most-cited &quot;never events&quot; in hospital care.
          Neuro patties are used in bulk during long cranial and spine cases, packed into deep and
          narrow spaces, and are the single easiest surgical sponge to lose count of. To mitigate
          the risk, every U.S.-sold neuro patty embeds a <strong>barium sulfate radiopaque line</strong>
          during manufacture. On an intraoperative or post-operative film, that line shows as a bright
          streak — invisible to the naked eye once soaked with blood, but unmistakable on X-ray.
        </p>
        <p className="text-slate-700 leading-relaxed mb-4">
          Regulatory / accreditation drivers requiring the barium line:
        </p>
        <ul className="space-y-2">
          {[
            "Joint Commission National Patient Safety Goal NPSG.14 — Prevent unintended retention of foreign objects",
            "AORN Guidelines for Prevention of Retained Surgical Items",
            "AAMI ANSI/AAMI ST79 — where reusable sponge-counting practices are referenced",
            "State-level Never-Event reporting under the CMS Serious Reportable Events list",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2 text-slate-700">
              <ShieldCheck className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </article>

      {/* ─────────── SECTION 5 — SIZE CHART ─────────── */}
      <article id="sizes" className="max-w-5xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-black text-slate-900 mb-2">
          Full size chart — FABCO ↔ American Surgical Ray-Cot<sup>®</sup>
        </h2>
        <p className="text-slate-600 mb-6">
          The most-ordered neuro patty sizes in U.S. hospitals, with the manufacturer part number for
          both brands and the typical clinical use for each size. Sizes without a FABCO cross-reference
          are stocked exclusively as American Surgical Ray-Cot<sup>®</sup>.
        </p>
        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-800">
              <tr>
                <th className="text-left px-3 py-3 font-bold">Size (in)</th>
                <th className="text-left px-3 py-3 font-bold">Size (mm)</th>
                <th className="text-left px-3 py-3 font-bold">Ray-Cot<sup>®</sup> #</th>
                <th className="text-left px-3 py-3 font-bold">FABCO #</th>
                <th className="text-left px-3 py-3 font-bold">Typical use</th>
              </tr>
            </thead>
            <tbody>
              {SIZE_CHART.map((r) => (
                <tr key={r.size} className="border-t border-slate-200 hover:bg-slate-50">
                  <td className="px-3 py-2 font-semibold text-slate-900">{r.size}</td>
                  <td className="px-3 py-2 font-mono text-slate-700">{r.mm}</td>
                  <td className="px-3 py-2 font-mono text-blue-800 font-bold">{r.ray_cot}</td>
                  <td className="px-3 py-2 font-mono text-slate-800">{r.fabco}</td>
                  <td className="px-3 py-2 text-slate-700">{r.use}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Standard packaging for every SKU on this chart: 10 patties per count-verification card · 1 card per sterile pouch · 20 pouches per box (200 patties per box).
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/marketplace/american-surgical"
            className="inline-flex items-center gap-2 px-5 h-11 rounded-lg bg-blue-800 hover:bg-blue-900 text-white font-bold"
            data-testid="size-cta-american-surgical"
          >
            <Package className="w-4 h-4" /> Order Ray-Cot<sup>®</sup> sizes
          </Link>
          <Link
            to="/marketplace/product/fabco-neuro-patties"
            className="inline-flex items-center gap-2 px-5 h-11 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
            data-testid="size-cta-fabco"
          >
            <ClipboardList className="w-4 h-4" /> Request FABCO pricing
          </Link>
        </div>
      </article>

      {/* ─────────── SECTION 6 — STERILIZATION ─────────── */}
      <article id="sterilization" className="max-w-4xl mx-auto px-6 py-8 bg-slate-50 rounded-2xl my-8">
        <h2 className="text-3xl font-black text-slate-900 mb-4">Sterilization & packaging</h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          Both American Surgical and FABCO ship neuro patties <strong>sterile from the
          manufacturer</strong> — hospitals do not sterilize them on-site. The industry-standard
          package pyramid is identical between the two brands:
        </p>
        <ol className="list-decimal ml-6 space-y-1 text-slate-700 mb-4">
          <li>10 patties → wound on a count-verification card</li>
          <li>1 card → sealed in a sterile peel-open pouch</li>
          <li>20 pouches → boxed = <strong>200 patties per box</strong></li>
        </ol>
        <p className="text-slate-700 leading-relaxed mb-4">
          Sterilization method: American Surgical primarily uses <strong>ethylene oxide (EO)</strong>
          per ISO 11135. Certain FABCO SKUs may be gamma-sterilized (ISO 11137) — always confirm
          the sterilization method for your specific SKU on the manufacturer&apos;s certificate of
          analysis if your facility has an EO-sensitivity policy.
        </p>
        <p className="text-slate-700 leading-relaxed">
          <strong>Shelf life</strong> is typically five years from the sterilization date printed on
          the box. Store between 20°–30°C, humidity below 60%, out of direct sunlight. Do not
          re-sterilize opened patties — they are single-use, and re-processing will destroy the
          locator-string bond and degrade the barium line.
        </p>
      </article>

      {/* ─────────── SECTION 7 — BRAND COMPARISON ─────────── */}
      <article id="brands" className="max-w-4xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-black text-slate-900 mb-4">
          FABCO vs American Surgical Ray-Cot<sup>®</sup> — how to choose
        </h2>
        <p className="text-slate-700 leading-relaxed mb-6">
          Both brands are U.S.-made, latex-free, X-ray detectable, sterile rayon patties in the
          same 200-per-box format. Clinical performance is essentially interchangeable — the
          decision is usually driven by surgeon protocol, GPO contract preference, or supplier
          diversity requirements. Here&apos;s the side-by-side:
        </p>
        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-800">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Attribute</th>
                <th className="text-left px-4 py-3 font-bold">American Surgical Ray-Cot<sup>®</sup></th>
                <th className="text-left px-4 py-3 font-bold">FABCO Neuro Patties</th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              {[
                ["Manufacturer", "American Surgical Company · Salem, MA (since 1919)", "FABCO · U.S. manufacturer"],
                ["Material", "100% rayon, latex-free", "100% rayon, latex-free"],
                ["Radiopaque marker", "Single barium sulfate line", "Barium sulfate line"],
                ["Locator string", "Yes — securely attached", "Yes — securely attached"],
                ["Absorbency (mfr claim)", "12% higher than the leading competitor", "Standard rayon absorbency"],
                ["Tensile strength (mfr claim)", "2× stronger than the leading competitor", "Standard rayon strength"],
                ["Packaging", "10/card · 20 pouches/box (200/box)", "10/card · 20 pouches/box (200/box)"],
                ["Sterilization", "EO (ISO 11135)", "EO or Gamma — SKU-dependent"],
                ["Sterile Mate role", "Authorized distributor", "Specialty (Off-The-Grid / non-GPO) stock"],
                ["Best for", "Facilities standardizing on the U.S. market leader", "Facilities with a specific FABCO contract or surgeon preference"],
              ].map(([a, r, f]) => (
                <tr key={a} className="border-t border-slate-200">
                  <td className="px-4 py-3 font-semibold text-slate-900">{a}</td>
                  <td className="px-4 py-3">{r}</td>
                  <td className="px-4 py-3">{f}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-5 text-slate-700 leading-relaxed">
          <strong>Sterile Mate&apos;s recommendation:</strong> if you&apos;re standardizing a new
          neurosurgery service or looking for the U.S. market-leader, order Ray-Cot<sup>®</sup>. If
          your GPO contract or a specific surgeon requires FABCO, we stock the 9 most-ordered FABCO
          SKUs and quote them on request. Either line ships from us the same way — same delivery
          window, same freight-inclusive pricing on 5+ box orders.
        </p>
      </article>

      {/* ─────────── SECTION 8 — PROCUREMENT ─────────── */}
      <article id="procurement" className="max-w-4xl mx-auto px-6 py-8 bg-slate-50 rounded-2xl my-8">
        <h2 className="text-3xl font-black text-slate-900 mb-4">How to buy neuro patties without a GPO</h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          Sterile Mate is a <strong>non-GPO friendly distributor</strong> — you do not need a Vizient,
          Premier, HealthTrust, or Intalere agreement to buy from us. That matters for three types
          of buyers:
        </p>
        <ul className="space-y-2 mb-4">
          {[
            "Ambulatory surgery centers and critical-access hospitals that don't have a full GPO contract",
            "Health systems with a GPO contract that doesn't cover a specific SKU (or is on allocation)",
            "Facilities using certified small-business supplier-diversity spend to hit compliance targets — we're Certified Small Business in Texas and Certified Small Business nationally",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2 text-slate-700">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-slate-700 leading-relaxed">
          Standard terms: Net 30 on credit approval, no minimum order for established accounts,
          freight-inclusive pricing on 5+ box orders in Texas / Louisiana / Alabama / Mississippi.
          certified small-business supplier-diversity credit is documented on every invoice.
        </p>
      </article>

      {/* ─────────── SECTION 9 — FAQ ─────────── */}
      <article id="faq" className="max-w-4xl mx-auto px-6 py-10">
        <h2 className="text-3xl font-black text-slate-900 mb-6">Frequently asked questions</h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details
              key={f.q}
              className="group border border-slate-200 rounded-lg bg-white overflow-hidden"
              data-testid={`neuro-faq-${i}`}
            >
              <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between hover:bg-slate-50">
                <span className="font-semibold text-slate-900">{f.q}</span>
                <ArrowRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
              </summary>
              <div className="px-5 pb-5 text-slate-700 leading-relaxed border-t border-slate-100 pt-3">
                {f.a}
              </div>
            </details>
          ))}
        </div>
      </article>

      {/* ─────────── FOOTER CTA BAND ─────────── */}
      <section className="bg-gradient-to-br from-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-14 text-center">
          <p className="text-[10px] font-black tracking-widest text-cyan-300 uppercase mb-3">
            Ready to order?
          </p>
          <h2 className="text-3xl md:text-4xl font-black mb-4">
            Both lines in stock. Same-day quote turnaround.
          </h2>
          <p className="text-white/85 mb-8 max-w-2xl mx-auto">
            Sterile Mate stocks the full American Surgical Ray-Cot<sup>®</sup> line as an authorized
            distributor and carries the nine most-ordered FABCO SKUs as a specialty item. Pick your
            brand and we&apos;ll return a firm quote within one business day.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">
              <Link to="/marketplace/american-surgical" data-testid="footer-cta-american-surgical">
                <Package className="w-5 h-5 mr-2" />
                Shop American Surgical
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
              <Link to="/marketplace/product/fabco-neuro-patties" data-testid="footer-cta-fabco">
                <ClipboardList className="w-5 h-5 mr-2" />
                Request FABCO Quote
              </Link>
            </Button>
            <a
              href="mailto:orders@sterilemate.com?subject=Neuro%20patty%20quote"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold"
            >
              <FileText className="w-4 h-4" /> orders@sterilemate.com
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
